import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import {
  DwExpressionStateService,
  DwLookupParameterUtilService,
  DwMetaDataFilterService,
  DwModalButtonStandardConfigs,
  DwModalConfig,
  DwModalService,
  DwModalSize,
  DwQueryFilterGroup
} from '@devwareapps/devware-cap';
import { forkJoin, of, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { FilterConfig } from '../../../../shared/models/filter-config.model';
import { DwChartDataSelectedEvent } from '../../../../z-devware/visualizations/models/dw-chart-data-selected-event.model';
import { DwTileConfig } from '../../../../z-devware/visualizations/models/dw-tile-config.model';
import {
  ReportDashboardDefinition,
  ReportDashboardFilterDefinition,
  ReportDrillDownDefinition,
  ReportDrillDownMapping,
  ReportMetricTileDefinition,
  ReportWidgetDefinition
} from '../../models/report-dashboard-definition.model';
import {
  ReportMetricTileResult,
  ReportSummaryResult,
  ReportWidgetResult
} from '../../models/report-dashboard-runtime.model';
import { ReportDashboardRepositoryService } from '../../services/report-dashboard-repository.service';
import { ReportsRepositoryService } from '../../../services/reports-repository.service';
import { AdminManagerService } from '../../../../shared/services/admin-manager.service';
import { PilexosUserTypeIds } from '../../../../shared/models/pilexos-user-type.enum';
import {
  ReportFilterPresetOwnershipType,
  ReportFilterPresetManageItem,
  ReportFilterPresetManageResult,
  ReportFilterPresetRecord,
  ReportGroupFilterSession,
  ReportSavedFilterState
} from '../../models/report-filter-preset.model';
import { ReportFilterPresetService } from '../../services/report-filter-preset.service';
import {
  ReportFilterPresetSaveModalComponent,
  ReportFilterPresetSaveModalResult
} from '../report-filter-preset-save-modal/report-filter-preset-save-modal.component';
import { ReportFilterPresetManageModalComponent } from '../report-filter-preset-manage-modal/report-filter-preset-manage-modal.component';
import { DwChartTileComponent } from '../../../../z-devware/visualizations/components/dw-chart-tile/dw-chart-tile.component';
import { GenericTagFiltersComponent } from '../../../../shared/components/generic-tag-filters/generic-tag-filters.component';
import { ActiveFilter } from '../../../../shared/models/active-filter.model';
import { ReportDashboardConfigService } from '../../services/report-dashboard-config.service';

@Component({
  selector: 'app-report-dashboard',
  templateUrl: './report-dashboard.component.html',
  styleUrls: ['./report-dashboard.component.scss']
})
export class ReportDashboardComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChildren(DwChartTileComponent) chartTiles: QueryList<DwChartTileComponent>;
  @ViewChild(GenericTagFiltersComponent) tagFilters: GenericTagFiltersComponent;
  @Input() definition: ReportDashboardDefinition;
  /** Carry compatible filters between tabs for this mounted dashboard group only. */
  @Input() filterPersistenceGroupKey: string;
  @Input() groupFilterSession: ReportGroupFilterSession = {};
  @Input() expressionContext: any;
  /** Additional host scope, retained when users change or clear dashboard filters. */
  @Input() scopeFilters: DwQueryFilterGroup;
  @Input() tileDrillDownRoute: string;
  @Input() initialTileSelection: ReportMetricTileResult;
  @Output() drillDownClosed = new EventEmitter<void>();
  @Input() showHeader = true;
  @Input() showDashboardTitle = false;
  /** Hides the saved-view picker and Charts/Data toggle row. Set to false for chromeless, tiles-only embeds with no configured filters. */
  @Input() showFilterBar = true;
  @Input() showChartGridFirst = false;
  @Input() canEditDashboard = false;
  @Output() showChartGridFirstChange = new EventEmitter<boolean>();
  @Output() editDashboardRequested = new EventEmitter<void>();
  @Output() dashboardDefaultsChanged = new EventEmitter<ReportDashboardDefinition>();

  filterConfigs: FilterConfig[] = [];
  tileResults: ReportMetricTileResult[] = [];
  widgetResults: ReportWidgetResult[] = [];
  customWidgets: ReportWidgetDefinition[] = [];
  skeletonTiles: ReportMetricTileDefinition[] = [];
  skeletonWidgets: ReportWidgetDefinition[] = [];
  currentFilters = new DwQueryFilterGroup();
  selectedLabel: string;
  selectedSelectionLabel: string;
  selectedDataValueDisplay: string;
  selectedDrillDownCount: number;
  gridFilters: any;
  showGrid = false;
  loading = false;
  presets: ReportFilterPresetRecord[] = [];
  defaultPresetId: number;
  selectedPresetId: number;
  selectedPreset: ReportFilterPresetRecord;
  appliedFilters: DwQueryFilterGroup;
  currentFilterState: ReportSavedFilterState;
  hasCarriedFilters = false;
  presetLoading = false;
  presetSaving = false;
  presetError: string;
  dataError: string;
  presetModified = false;
  drillDownLoading = false;
  drillDownMessage: string;
  selectedWidgetKey: string;
  selectedDrillDown: ReportDrillDownDefinition;
  printActiveFilters: ActiveFilter[] = [];
  readonly chartDataToggleOptions: any = {
    displayStyle: 'blockButtons',
    stateMode: 'twoState',
    trueLabel: 'Charts',
    falseLabel: 'Data'
  };

  private destroy$ = new Subject<void>();
  private loadGeneration = 0;
  private drillDownGeneration = 0;
  private filterChangeGeneration = 0;
  private presetLoadGeneration = 0;
  private filterSessionKey: string;
  private filtersWereEdited = false;
  private viewSelectionGeneration = 0;
  private readonly maxDrillDownKeys = 1000;
  private pendingDrillDownResolution: {
    sourceQuery: any;
    filters: any;
    sourceKeyField: string;
    drillDown: ReportDrillDownDefinition;
  };

  constructor(
    private router: Router,
    private repository: ReportDashboardRepositoryService,
    private expressionState: DwExpressionStateService,
    private reportsRepository: ReportsRepositoryService,
    private configService: ReportDashboardConfigService,
    private presetService: ReportFilterPresetService,
    private adminManager: AdminManagerService,
    private modalService: DwModalService,
    private metaDataFilterService: DwMetaDataFilterService,
    private lookupParameterUtilService: DwLookupParameterUtilService
  ) { }

  ngOnInit(): void {
    document.body.classList.add('report-dashboard-page');
    this.reportsRepository.getCloseGridObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.closeGrid());
    this.initializeDefinition();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.definition && !changes.definition.firstChange) {
      this.initializeDefinition();
    }
    if (((changes.expressionContext && !changes.expressionContext.firstChange)
      || (changes.scopeFilters && !changes.scopeFilters.firstChange))
      && !changes.definition) {
      this.initializeDefinition();
    }
    if (changes.showChartGridFirst && !changes.showChartGridFirst.firstChange) {
      this.widgetResults = this.applyChartDisplayPreference(this.widgetResults);
    }
  }

  ngOnDestroy(): void {
    document.body.classList.remove('report-dashboard-printing');
    document.body.classList.remove('report-dashboard-page');
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFiltersChanged(filters: DwQueryFilterGroup): void {
    const generation = ++this.filterChangeGeneration;
    const dashboardKey = this.definition?.key;

    // The filter component can resolve configured defaults synchronously during
    // its initial change-detection pass. Apply the emitted filters after that
    // pass so parent bindings are not changed while Angular is checking them.
    Promise.resolve().then(() => {
      if (generation !== this.filterChangeGeneration || dashboardKey !== this.definition?.key) {
        return;
      }

      this.currentFilters = filters || new DwQueryFilterGroup();
      this.rememberGroupFilters();
      this.presetModified = !!this.selectedPreset;
      this.resetDrillDown();
      this.loadDashboard();
    });
  }

  onCustomFilterStateChanged(filters: any[]): void {
    const dashboardKey = this.definition?.key;
    const viewSelectionGeneration = this.viewSelectionGeneration;

    Promise.resolve().then(() => {
      if (dashboardKey === this.definition?.key && viewSelectionGeneration === this.viewSelectionGeneration) {
        this.currentFilterState = filters?.length ? { schemaVersion: 1, filters } : null;
      }
    });
  }

  onFiltersEdited(): void {
    this.filtersWereEdited = true;
    const generation = this.filterChangeGeneration;
    // The companion query and semantic-date outputs settle first.
    Promise.resolve().then(() => {
      if (generation === this.filterChangeGeneration) {
        this.rememberGroupFilters();
      }
    });
  }

  private rememberGroupFilters(): void {
    if (this.filtersWereEdited && this.filterSessionKey) {
      this.groupFilterSession[this.filterSessionKey] = JSON.parse(JSON.stringify({
        filterGroup: this.currentFilters,
        filterState: this.currentFilterState
      }));
    }
  }

  selectPreset(presetId: number, userInitiated = true): void {
    ++this.viewSelectionGeneration;
    ++this.filterChangeGeneration;
    this.filtersWereEdited = false;
    // A view's built-in filters are a local baseline, not a user filter edit.
    // Explicitly choosing/resetting a view ends carryover for this open group.
    Object.keys(this.groupFilterSession).forEach(key => {
      if (key === this.filterSessionKey
        || (userInitiated && JSON.parse(key)[0] === this.filterPersistenceGroupKey)) {
        delete this.groupFilterSession[key];
      }
    });
    this.hasCarriedFilters = false;
    const preset = this.presets.find(item => item.ReportFilterPresetId === Number(presetId));
    this.selectedPresetId = preset?.ReportFilterPresetId;
    this.selectedPreset = preset;
    this.presetError = null;
    this.presetModified = false;

    if (!preset) {
      this.currentFilterState = null;
      this.currentFilters = new DwQueryFilterGroup();
      this.appliedFilters = this.currentFilters;
      this.resetDrillDown();
      this.loadDashboard();
      return;
    }

    try {
      const resolved = this.presetService.resolvePreset(preset);
      this.currentFilters = resolved.filterGroup;
      this.currentFilterState = resolved.filterState;
      this.appliedFilters = resolved.filterGroup;
      this.resetDrillDown();
      this.loadDashboard();
    } catch (error) {
      this.presetError = error?.message || error || 'The saved view could not be applied.';
    }
  }

  resetPreset(): void {
    if (this.selectedPreset) {
      this.selectPreset(this.selectedPreset.ReportFilterPresetId);
    }
  }

  saveView(updateExisting: boolean, editMetadataOnly = false): void {
    if (updateExisting && !this.selectedPreset) {
      this.saveDashboardDefaults();
      return;
    }

    const existing = updateExisting ? this.selectedPreset : null;
    const user = this.adminManager.getPilexosUser();
    const modalConfig = new DwModalConfig();
    modalConfig.title = updateExisting ? 'Update Saved View' : 'Save Report View';
    modalConfig.component = ReportFilterPresetSaveModalComponent;
    modalConfig.buttons = DwModalButtonStandardConfigs.instance.SaveAndCancel;
    modalConfig.modalSize = DwModalSize.medium;
    modalConfig.cloneData = true;
    modalConfig.data = {
      name: existing?.PresetName || '',
      canShareWithFlightSchool: user?.UserTypeId === PilexosUserTypeIds.flightSchool,
      canSaveSystem: this.adminManager.canUseAdminProperties(),
      updating: !!existing,
      ownershipType: existing?.ReportFilterPresetOwnershipTypeId || ReportFilterPresetOwnershipType.User
    };

    this.modalService.showModal(modalConfig).subscribe(result => {
      if (result.cancelled || !result.data) {
        return;
      }
      const values = result.data as ReportFilterPresetSaveModalResult;
      const savedDefinition = editMetadataOnly && existing
        ? this.presetService.resolvePreset(existing)
        : null;
      this.presetSaving = true;
      this.presetError = null;
      this.presetService.savePreset(
        this.definition.key,
        values.name,
        values.ownershipType,
        savedDefinition?.filterGroup || this.currentFilters,
        savedDefinition?.filterState || this.currentFilterState,
        existing
      ).subscribe(saved => {
        this.presetSaving = false;
        this.loadPresets(saved.ReportFilterPresetId);
      }, error => {
        this.presetSaving = false;
        this.presetError = error?.message || error || 'The report view could not be saved.';
      });
    });
  }

  editSelectedView(): void {
    if (this.canEditSelectedPreset) {
      this.saveView(true, true);
    }
  }

  manageViews(): void {
    const manageablePresets = this.presets.filter(preset => this.canManagePreset(preset));
    const modalConfig = new DwModalConfig();
    modalConfig.title = 'Manage Saved Views';
    modalConfig.component = ReportFilterPresetManageModalComponent;
    modalConfig.buttons = DwModalButtonStandardConfigs.instance.SaveAndCancel;
    modalConfig.modalSize = DwModalSize.large;
    modalConfig.cloneData = true;
    modalConfig.data = {
      views: [
        {
          name: 'Dashboard defaults',
          sortOrder: 0,
          isDashboardDefault: true,
          isDefault: !this.defaultPresetId,
          canManage: false
        } as ReportFilterPresetManageItem,
        ...this.presets.map(preset => ({
          presetId: preset.ReportFilterPresetId,
          name: preset.PresetName,
          ownershipType: preset.ReportFilterPresetOwnershipTypeId,
          sortOrder: preset.SortOrder,
          isDefault: preset.ReportFilterPresetId === this.defaultPresetId,
          canManage: this.canManagePreset(preset)
        } as ReportFilterPresetManageItem))
      ]
    };

    this.modalService.showModal(modalConfig).subscribe(result => {
      if (result.cancelled || !result.data) {
        return;
      }

      const changes = result.data as ReportFilterPresetManageResult;
      const selectedViewRemoved = changes.removedPresetIds.indexOf(this.selectedPresetId) >= 0;
      this.presetSaving = true;
      this.presetError = null;
      forkJoin([
        this.presetService.applyPresetManagement(manageablePresets, changes),
        changes.defaultChanged
          ? this.presetService.setDefaultPreset(this.definition.key, changes.defaultPresetId)
          : of(null)
      ])
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.presetSaving = false;
          const nextPresetId = selectedViewRemoved ? changes.defaultPresetId : this.selectedPresetId;
          this.loadPresets(nextPresetId, selectedViewRemoved && !nextPresetId);
        }, error => {
          this.presetSaving = false;
          this.presetError = error?.message || error || 'The saved views could not be updated.';
          this.loadPresets(this.selectedPresetId);
        });
    });
  }

  presetsByOwnership(ownershipType: ReportFilterPresetOwnershipType): ReportFilterPresetRecord[] {
    return this.presets.filter(preset => preset.ReportFilterPresetOwnershipTypeId === ownershipType);
  }

  get selectedViewDisplay(): string {
    if (this.hasCarriedFilters && !this.selectedPreset) {
      return 'Current group filters';
    }
    const name = this.selectedPreset?.PresetName || 'Dashboard defaults';
    return this.selectedPreset && this.presetModified ? `${name} (Modified)` : name;
  }

  readonly PresetOwnershipType = ReportFilterPresetOwnershipType;

  get canEditSelectedPreset(): boolean {
    return this.canManagePreset(this.selectedPreset);
  }

  get canSaveSelectedViewChanges(): boolean {
    if (this.presetSaving) {
      return false;
    }
    return this.selectedPreset
      ? this.canEditSelectedPreset && this.presetModified
      : this.canEditDashboard;
  }

  get selectedViewIsDefault(): boolean {
    return (this.selectedPresetId || null) === (this.defaultPresetId || null);
  }

  makeSelectedViewDefault(): void {
    if (this.selectedViewIsDefault || this.presetSaving) {
      return;
    }

    this.presetSaving = true;
    this.presetError = null;
    const dashboardKey = this.definition.key;
    const presetId = this.selectedPresetId;
    this.presetService.setDefaultPreset(dashboardKey, presetId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.presetSaving = false;
        if (dashboardKey === this.definition.key) {
          this.defaultPresetId = presetId || null;
        }
      }, error => {
        this.presetSaving = false;
        this.presetError = error?.message || error || 'The default report view could not be saved.';
      });
  }

  private canManagePreset(preset: ReportFilterPresetRecord): boolean {
    if (!preset) {
      return false;
    }
    const user = this.adminManager.getPilexosUser();
    if (this.adminManager.canUseAdminProperties()) {
      return true;
    }
    if (preset.ReportFilterPresetOwnershipTypeId === ReportFilterPresetOwnershipType.User) {
      return preset.OwnerUserId === user?.user?.UserId;
    }
    return preset.ReportFilterPresetOwnershipTypeId === ReportFilterPresetOwnershipType.FlightSchool
      && user?.UserTypeId === PilexosUserTypeIds.flightSchool
      && preset.FlightSchoolOrganizationId === user?.flightSchoolOrganizationId;
  }

  presetGroupLabel(preset: ReportFilterPresetRecord): string {
    switch (preset.ReportFilterPresetOwnershipTypeId) {
      case ReportFilterPresetOwnershipType.System: return 'Recommended';
      case ReportFilterPresetOwnershipType.FlightSchool: return 'Flight School';
      default: return 'My Views';
    }
  }

  onDataSelected(event: DwChartDataSelectedEvent<any>, result: ReportWidgetResult): void {
    const mapping = this.getDrillDownMapping(result.definition.drillDown, result.definition.drillDownKey);
    const drillDown = this.getVisibleDrillDown(mapping?.drillDownKey || result.definition.drillDownKey || this.definition.defaultDrillDownKey);
    if (!drillDown) {
      return;
    }

    const selectionParts = [event.category || 'Details'];
    if (event.series?.legendDisplay && event.series.legendDisplay !== event.category) {
      selectionParts.push(event.series.legendDisplay);
    }
    this.selectedSelectionLabel = selectionParts.join(' · ');
    this.selectedLabel = `${result.definition.title} - ${this.selectedSelectionLabel}`;
    this.selectedDataValueDisplay = this.formatSelectedDataValue(event.dataValue, event.series?.valueSuffix);
    this.selectedDrillDownCount = null;
    this.selectedWidgetKey = result.definition.key;
    this.selectedDrillDown = drillDown;
    this.pendingDrillDownResolution = null;
    this.gridFilters = null;
    this.drillDownLoading = false;
    this.drillDownMessage = null;
    ++this.drillDownGeneration;

    if (event.isDoubleClick) {
      this.showGrid = true;
    } else if (event.browserEvent?.ctrlKey) {
      this.showGrid = !this.showGrid;
    }

    const filters = this.repository.buildDrillDownFilters(result, event);
    if (!mapping?.sourceKeyField || !drillDown.targetKeyField) {
      this.gridFilters = result.query?.Filter?.Filters?.length
        ? { groupReportFilters: result.query.Filter, ...filters }
        : filters;
      this.drillDownLoading = false;
      this.drillDownMessage = null;
      return;
    }

    this.pendingDrillDownResolution = {
      sourceQuery: result.query,
      filters: filters,
      sourceKeyField: mapping.sourceKeyField,
      drillDown: drillDown
    };

    if (this.showGrid) {
      this.openSelectedDetails();
    }
  }

  onTileSelected(result: ReportMetricTileResult): void {
    if (this.tileDrillDownRoute) {
      // Normalized definitions contain display callbacks, which browser history
      // cannot clone. The drill-down needs only their serializable query/config.
      const reportTileDrillDown = JSON.parse(JSON.stringify({ definition: this.definition, selection: result }));
      this.router.navigateByUrl(this.tileDrillDownRoute, {
        state: { reportTileDrillDown }
      });
      return;
    }
    const options = result.definition.additionalConfig;
    const mapping = this.getDrillDownMapping(options.drillDown, options.drillDownKey);
    const drillDown = this.getVisibleDrillDown(mapping?.drillDownKey || options.drillDownKey || this.definition.defaultDrillDownKey);
    if (!drillDown) {
      return;
    }

    const filters: any = {};
    if (options.detailFilter?.Filters?.length) {
      filters.groupTileDetailFilters = options.detailFilter;
    }

    this.selectedLabel = result.definition.tileDisplay;
    this.selectedWidgetKey = null;
    this.selectedDrillDown = drillDown;
    this.showGrid = true;

    if (!mapping?.sourceKeyField || !drillDown.targetKeyField) {
      this.gridFilters = result.summaryResult?.query?.Filter?.Filters?.length
        ? { groupSummaryFilters: result.summaryResult.query.Filter, ...filters }
        : filters;
      this.drillDownLoading = false;
      this.drillDownMessage = null;
      return;
    }

    this.resolveDrillDownKeys(result.summaryResult.query, filters, mapping.sourceKeyField, drillDown);
  }

  printReport(): void {
    if (!this.loading) {
      window.print();
    }
  }

  @HostListener('window:beforeprint')
  prepareChartsForPrint(): void {
    this.printActiveFilters = (this.tagFilters?.activeFilters || []).map(filter => ({ ...filter }));
    document.body.classList.add('report-dashboard-printing');
    this.resizeChartsForCurrentLayout(true);
  }

  private saveDashboardDefaults(): void {
    if (!this.canEditDashboard || this.presetSaving) {
      return;
    }

    this.presetSaving = true;
    this.presetError = null;
    const activeFilters = (this.tagFilters?.activeFilters || []).map(filter => ({ ...filter }));
    this.configService.saveDashboardDefaultFilters(this.definition.key, activeFilters)
      .pipe(takeUntil(this.destroy$))
      .subscribe(definition => {
        this.presetSaving = false;
        this.presetModified = false;
        this.dashboardDefaultsChanged.emit(definition);
      }, error => {
        this.presetSaving = false;
        this.presetError = error?.message || error || 'The dashboard default filters could not be saved.';
      });
  }

  @HostListener('window:afterprint')
  restoreChartsAfterPrint(): void {
    document.body.classList.remove('report-dashboard-printing');
    this.resizeChartsForCurrentLayout(false);
  }

  onShowChartGridFirstChanged(showGridFirst: boolean): void {
    this.showChartGridFirstChange.emit(!!showGridFirst);
  }

  openSelectedDetails(): void {
    if (this.gridFilters) {
      this.showGrid = true;
      return;
    }

    if (this.pendingDrillDownResolution && !this.drillDownLoading) {
      const request = this.pendingDrillDownResolution;
      this.showGrid = true;
      this.resolveDrillDownKeys(request.sourceQuery, request.filters, request.sourceKeyField, request.drillDown);
    }
  }

  get canOpenSelectedDetails(): boolean {
    return !!this.gridFilters || !!this.pendingDrillDownResolution;
  }

  clearSelectedDetails(): void {
    this.resetDrillDown();
  }

  closeGrid(): void {
    this.showGrid = false;
    this.drillDownClosed.emit();
  }

  get currentDrillDown() {
    if (this.selectedDrillDown && this.isVisible(this.selectedDrillDown.visibleExpression)) {
      return this.selectedDrillDown;
    }
    const visibleDrillDowns = (this.definition.drillDowns || [])
      .filter(item => this.isVisible(item.visibleExpression));
    return visibleDrillDowns.find(item => item.key === this.definition.defaultDrillDownKey)
      || visibleDrillDowns[0];
  }

  get widgetGridColumnCount(): number {
    return this.definition?.layoutColumnCount || this.definition?.columnCount || 2;
  }

  widgetColumnSpan(widget: ReportWidgetDefinition): number {
    if (widget.columnSpan) {
      return widget.columnSpan;
    }
    if (this.definition?.layoutColumnCount) {
      const legacyColumns = this.definition.columnCount || 2;
      return Math.max(1, Math.floor(this.definition.layoutColumnCount / legacyColumns));
    }
    return 1;
  }

  private getVisibleDrillDown(key: string) {
    return (this.definition.drillDowns || [])
      .find(item => item.key === key && this.isVisible(item.visibleExpression));
  }

  private initializeDefinition(): void {
    if (!this.definition) {
      return;
    }

    const nextFilterConfigs = (this.definition.filters || [])
      .filter(filter => filter.config.showFilter)
      .map(filter => this.toFilterConfig(filter));
    const filterSignature = this.getFilterCompatibilitySignature(nextFilterConfigs);
    this.filterSessionKey = this.filterPersistenceGroupKey && nextFilterConfigs.length
      && !this.initialTileSelection
      ? JSON.stringify([this.filterPersistenceGroupKey, filterSignature]) : null;
    const savedFilters = this.filterSessionKey ? this.groupFilterSession[this.filterSessionKey] : null;
    const carryFilters = !!savedFilters;
    this.filtersWereEdited = carryFilters;

    ++this.filterChangeGeneration;
    this.printActiveFilters = [];
    const carriedFilters = new DwQueryFilterGroup();
    if (carryFilters) {
      Object.assign(carriedFilters, JSON.parse(JSON.stringify(savedFilters.filterGroup)));
    }
    this.currentFilters = carriedFilters;
    this.appliedFilters = this.currentFilters;
    this.currentFilterState = savedFilters?.filterState
      ? JSON.parse(JSON.stringify(savedFilters.filterState)) : null;
    this.hasCarriedFilters = carryFilters;
    this.selectedPresetId = null;
    this.selectedPreset = null;
    this.defaultPresetId = null;
    this.presetModified = false;
    this.resetDrillDown();
    this.filterConfigs = nextFilterConfigs;
    if (this.initialTileSelection) {
      // Use the originating tile's exact source query, including its scope and
      // static filters. Saved report defaults must not change this population.
      this.onTileSelected(this.initialTileSelection);
      return;
    }
    this.loadDashboard();
    this.loadPresets(undefined, false, !carryFilters);
  }

  private getFilterCompatibilitySignature(configs: FilterConfig[]): string {
    // Compare semantics, not labels, ordering, or dashboard-specific defaults.
    return JSON.stringify({
      // Qualified attributes already identify their source. Unqualified ones need the dashboard source.
      source: configs.some(config => typeof config.attribute === 'string' && config.attribute.indexOf('.') < 0)
        ? this.definition.dataSourceItemName : null,
      filters: configs.map(config => JSON.stringify({
        field: config.field,
        attribute: config.attribute,
        type: config.type,
        filterTypeKey: config.filterTypeKey || null,
        operator: config.operator == null ? null : config.operator,
        lookupId: config.lookupId == null ? null : String(config.lookupId),
        lookupParams: config.lookupParams || [],
        lookupParamNames: config.lookupParamNames || [],
        allowMultiple: !!config.allowMultiple,
        options: (config.options || []).map(option => option.value)
      })).sort()
    });
  }

  private loadPresets(selectPresetId?: number, selectDefaults = false, selectUserDefault = false): void {
    const generation = ++this.presetLoadGeneration;
    const dashboardKey = this.definition.key;
    const viewSelectionGeneration = this.viewSelectionGeneration;
    this.presetLoading = true;
    forkJoin([
      this.presetService.loadPresets(dashboardKey),
      this.presetService.loadPreference(dashboardKey)
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([presets, preference]) => {
        if (generation !== this.presetLoadGeneration || dashboardKey !== this.definition.key) {
          return;
        }

        this.presets = presets;
        const preferredPresetId = preference?.DefaultReportFilterPresetId;
        this.defaultPresetId = presets.some(preset => preset.ReportFilterPresetId === preferredPresetId)
          ? preferredPresetId
          : null;
        this.presetLoading = false;
        if (selectDefaults) {
          this.selectPreset(null, false);
        } else if (selectPresetId) {
          this.selectPreset(selectPresetId, false);
        } else if (selectUserDefault && this.defaultPresetId && !this.filtersWereEdited
          && viewSelectionGeneration === this.viewSelectionGeneration) {
          this.selectPreset(this.defaultPresetId, false);
        }
      }, error => {
        if (generation !== this.presetLoadGeneration || dashboardKey !== this.definition.key) {
          return;
        }
        this.presetLoading = false;
        this.presetError = error?.message || error || 'Saved report views could not be loaded.';
      });
  }

  private loadDashboard(): void {
    const generation = ++this.loadGeneration;
    const visibleTiles = (this.definition.tiles || [])
      .filter(tile => !tile.hideTile && this.isVisible(tile.visibleExpression));
    const visibleSummaryKeys = visibleTiles.map(tile => tile.additionalConfig.summaryKey);
    const summaries = (this.definition.summaries || [])
      .filter(summary => visibleSummaryKeys.indexOf(summary.key) >= 0);
    const widgets = (this.definition.widgets || []).filter(widget => this.isVisible(widget.visibleExpression));
    this.customWidgets = widgets.filter(widget => widget.type === 'custom' && !!widget.componentName);
    this.skeletonTiles = visibleTiles;
    this.skeletonWidgets = widgets;

    const queryFilters = new DwQueryFilterGroup();
    if (this.scopeFilters?.Filters?.length) {
      queryFilters.AddFilterAnd(this.scopeFilters);
    }
    if (this.currentFilters?.Filters?.length) {
      queryFilters.AddFilterAnd(this.currentFilters);
    }

    this.loading = true;
    this.dataError = null;
    this.tileResults = [];
    this.widgetResults = [];
    forkJoin([
      this.repository.loadSummaries(
        summaries,
        queryFilters,
        this.definition.globalFilters,
        this.expressionContext
      ),
      this.repository.loadWidgets(
        widgets,
        queryFilters,
        this.definition.globalFilters,
        this.expressionContext
      )
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([summaryResults, widgetResults]) => {
        if (generation !== this.loadGeneration) {
          return;
        }
        this.tileResults = visibleTiles.map(tile => this.buildTileResult(tile, summaryResults));
        this.widgetResults = this.applyChartDisplayPreference(widgetResults);
        this.loading = false;
      }, error => {
        if (generation !== this.loadGeneration) {
          return;
        }
        this.loading = false;
        this.dataError = error?.message || error || 'The dashboard data could not be loaded.';
      });
  }

  private applyChartDisplayPreference(results: ReportWidgetResult[]): ReportWidgetResult[] {
    return (results || []).map(result => {
      const baseShowGridFirst = result.chartPreferences?.baseShowGridFirst
        ?? !!result.chartPreferences?.showGridFirst;

      return {
        ...result,
        chartPreferences: {
          ...result.chartPreferences,
          baseShowGridFirst,
          showGridFirst: this.showChartGridFirst || baseShowGridFirst
        }
      };
    });
  }

  private resizeChartsForCurrentLayout(forPrint: boolean): void {
    const reportElement = document.querySelector('.report-dashboard') as HTMLElement;
    reportElement?.getBoundingClientRect();
    this.chartTiles?.forEach(chartTile => chartTile.resizeChart(forPrint));
  }

  private buildTileResult(tile: ReportMetricTileDefinition, summaries: ReportSummaryResult[]): ReportMetricTileResult {
    const options = tile.additionalConfig;
    const summaryResult = summaries.find(summary => summary.key === options.summaryKey);
    const row = summaryResult?.row || {};
    const summaryRows = summaries.reduce((result, summary) => ({ ...result, [summary.key]: summary.row }), {});
    let value: number;

    if (options.value.field) {
      value = Number(row[options.value.field]) || 0;
    } else {
      const numerator = Number(row[options.value.numeratorField]) || 0;
      const denominator = Number(row[options.value.denominatorField]) || 0;
      value = denominator > 0 ? numerator / denominator : 0;
    }

    const formattedValue = options.format === 'percent'
      ? `${Math.round(value * 100)}%`
      : options.format === 'currency'
        ? value.toLocaleString('en-US', { style: 'currency', currency: options.currencyCode || 'USD' })
        : value.toLocaleString('en-US', { maximumFractionDigits: options.format === 'decimal' ? 2 : 0 });

    const tileConfig: DwTileConfig = {
      containerTitle: tile.tileDisplay,
      showContainerTitle: true,
      iconClass: tile.tileIconClass,
      tileText: formattedValue,
      additionalText: options.supportingText?.(row, formattedValue, summaryRows)
    };

    return { definition: tile, tileConfig, summaryResult };
  }

  private toFilterConfig(definition: ReportDashboardFilterDefinition): FilterConfig {
    const config = definition.config;
    const ui = config.additionalConfig;
    const lookupParameterReferences = config.lookupConfig?.lookupParamAttributeOverrides || [];
    const lookupParams = lookupParameterReferences.length
      ? this.lookupParameterUtilService.evalLookupParameterExpressions(
        lookupParameterReferences,
        {},
        this.expressionContext || {}
      )
      : ui.lookupParams;
    const defaultValue = this.metaDataFilterService.getDefaultValue(
      config.hasDefaultValue,
      config.defaultValue,
      this.expressionContext || {}
    );
    return {
      field: config.attr,
      label: config.label || config.filterDisplay || config.attr,
      type: ui.presentationType,
      filterTypeKey: ui.filterTypeKey,
      operator: config.filterType,
      lookupId: ui.lookupId,
      lookupParams,
      lookupParamNames: ui.lookupParamNames,
      defaultToFirstLookupOption: ui.defaultToFirstLookupOption,
      options: ui.options,
      attribute: config.attr,
      defaultValue,
      placeholder: config.placeholderText,
      allowMultiple: ui.allowMultiple,
      iconClass: ui.iconClass
    };
  }

  private isVisible(expression: any): boolean {
    if (!expression) {
      return true;
    }

    return this.expressionState.evaluateExpressionWithResolvedData(expression, {}, this.expressionContext || {});
  }

  private getDrillDownMapping(
    mapping?: ReportDrillDownMapping,
    legacyDrillDownKey?: string
  ): ReportDrillDownMapping {
    const defaultMapping = this.definition.defaultDrillDown;
    const drillDownKey = mapping?.drillDownKey
      || legacyDrillDownKey
      || defaultMapping?.drillDownKey
      || this.definition.defaultDrillDownKey;
    const sourceKeyField = mapping?.sourceKeyField || defaultMapping?.sourceKeyField;

    return drillDownKey ? { drillDownKey, sourceKeyField } : null;
  }

  private resolveDrillDownKeys(
    sourceQuery: any,
    filters: any,
    sourceKeyField: string,
    drillDown: ReportDrillDownDefinition
  ): void {
    const generation = ++this.drillDownGeneration;
    this.gridFilters = null;
    this.drillDownLoading = true;
    this.drillDownMessage = null;
    this.selectedDrillDownCount = null;

    this.repository.resolveDrillDownKeys(sourceQuery, filters, sourceKeyField, this.maxDrillDownKeys)
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (generation !== this.drillDownGeneration) {
          return;
        }

        this.drillDownLoading = false;
        if (result.tooMany) {
          this.pendingDrillDownResolution = null;
          this.drillDownMessage = 'Too many records to display. Refine the dashboard filters or select a more specific chart value.';
          return;
        }
        if (!result.keys.length) {
          this.pendingDrillDownResolution = null;
          this.selectedDrillDownCount = 0;
          this.drillDownMessage = `No matching ${drillDown.title.toLowerCase()} were found.`;
          return;
        }

        this.selectedDrillDownCount = result.keys.length;
        this.pendingDrillDownResolution = null;
        this.gridFilters = this.repository.buildTargetKeyFilters(drillDown.targetKeyField, result.keys);
      }, () => {
        if (generation !== this.drillDownGeneration) {
          return;
        }
        this.drillDownLoading = false;
        this.drillDownMessage = `${drillDown.title} could not be loaded. Please try again.`;
      });
  }

  private resetDrillDown(): void {
    ++this.drillDownGeneration;
    this.showGrid = false;
    this.gridFilters = null;
    this.drillDownLoading = false;
    this.drillDownMessage = null;
    this.selectedLabel = null;
    this.selectedSelectionLabel = null;
    this.selectedDataValueDisplay = null;
    this.selectedDrillDownCount = null;
    this.selectedWidgetKey = null;
    this.selectedDrillDown = null;
    this.pendingDrillDownResolution = null;
  }

  private formatSelectedDataValue(value: any, suffix: string): string {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const displayValue = value.toString();
    return suffix && !displayValue.endsWith(suffix)
      ? `${displayValue}${suffix}`
      : displayValue;
  }

}
